// The AI research dossier for a lead: what enrichment actually bought. Kept
// per account in localStorage so the lead page renders instantly and offline,
// and backfilled from /api/enrich/lead when a teammate (or another browser) is
// the one who paid for the research.
//
// enrichedLeads.js answers "is this row enriched?"; this answers "what did we
// learn?". Keyed the same way — the lead's row key (keyOf).
import { scopedKey } from './userScope.js'
import { isEnriched, rowEnrichedFlag } from './enrichedLeads.js'

const STORAGE_BASE = 'revailosales.enrichment'
const storageKey = () => scopedKey(STORAGE_BASE)
// Dossiers are a few KB each; cap the store so localStorage can't fill up.
const MAX_ENTRIES = 400

const listeners = new Set()
let cache = null
let cacheKey = null

function load() {
  const key = storageKey()
  if (cache && cacheKey === key) return cache
  cacheKey = key
  try {
    cache = JSON.parse(localStorage.getItem(key) || '{}')
  } catch {
    cache = {}
  }
  return cache
}

function persist(map) {
  const entries = Object.entries(map)
  // Oldest-first eviction by savedAt.
  if (entries.length > MAX_ENTRIES) {
    entries.sort((a, b) => (b[1]?.savedAt || '').localeCompare(a[1]?.savedAt || ''))
    map = Object.fromEntries(entries.slice(0, MAX_ENTRIES))
    cache = map
  }
  try {
    localStorage.setItem(cacheKey, JSON.stringify(map))
  } catch {
    /* quota — the API copy is still authoritative */
  }
  return map
}

// dossier: { result, rankedScore, reasoning, phones, contactsNote, enrichedAt, cached }
export function saveEnrichment(rowKey, dossier) {
  if (!rowKey || !dossier) return
  const map = load()
  map[rowKey] = { ...dossier, savedAt: new Date().toISOString() }
  persist(map)
  listeners.forEach((fn) => fn(rowKey))
}

export function getEnrichment(rowKey) {
  return (rowKey && load()[rowKey]) || null
}

export function subscribeEnrichment(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

// ── shape helpers (the dossier is model output, so treat every field as maybe) ─
export const asList = (v) =>
  Array.isArray(v)
    ? v.map((x) => String(x).trim()).filter(Boolean)
    : String(v || '')
        .split(/[;\n]|(?:,\s(?=[A-Z]))/)
        .map((s) => s.trim())
        .filter(Boolean)

// The model sometimes ignores "plain text only" and emits markdown links or
// tracking URLs mid-sentence — scrub them so prose stays prose. Keeps the
// visible label of a link, drops parenthesised link duplicates and utm junk.
export function cleanText(value) {
  let t = String(value ?? '')
  t = t.replace(/\(\s*\[[^\]]*\]\([^)]*\)\s*\)/g, '') // ([label](url)) → gone
  t = t.replace(/\[([^\]]*)\]\(([^)]*)\)/g, '$1') // [label](url) → label
  t = t.replace(/\(?\bhttps?:\/\/[^\s)]*utm_source=[^\s)]*\)?/gi, '') // tracking URLs
  return t.replace(/\s{2,}/g, ' ').replace(/\s+([,.;:])/g, '$1').trim()
}

// Research answers often lead with a verdict: "yes — Jane Doe (Owner, per x.com)"
// or "unknown — no filings found". Split the verdict off so the UI can style it.
export function splitVerdict(value) {
  const text = cleanText(value)
  const m = /^(yes|no|unknown|none)\b[\s—–:-]*(.*)$/is.exec(text)
  if (!m) return { verdict: '', detail: text }
  return { verdict: m[1].toLowerCase(), detail: m[2].trim() }
}

// "(per linkedin.com)" → the citation, so it can render as a quiet source tag.
export function splitCitation(value) {
  const text = cleanText(value)
  const m = /\(per\s+([^)]+)\)\s*$/i.exec(text)
  if (!m) return { text, source: '' }
  return { text: text.slice(0, m.index).trim(), source: m[1].trim() }
}

// Anything the model couldn't establish comes back as some flavour of unknown;
// those shouldn't take up space in the UI.
export function isKnown(value) {
  if (value == null) return false
  if (Array.isArray(value)) return value.length > 0
  const s = String(value).trim()
  if (!s) return false
  return !/^(unknown|n\/?a|none|not (provided|found|available)|—|-)$/i.test(s)
}

// ── re-enrichment policy ────────────────────────────────────────────────────
// Re-running costs another full research pass (4 web-search calls + scoring +
// Apollo), and a company's site, reviews and job postings do not meaningfully
// change hour to hour. So a dossier is considered fresh for a day: re-enriching
// is refused under 24h, and allowed after that only on explicit confirmation.
export const REENRICH_AFTER_MS = 24 * 60 * 60 * 1000

// Millis since this lead was researched, or null when we have no dossier.
export function enrichedAgeMs(rowKey, dossier) {
  const d = dossier || getEnrichment(rowKey)
  if (!d) return null
  const stamp = d.enrichedAt || d.savedAt
  const t = stamp ? new Date(stamp).getTime() : NaN
  if (Number.isNaN(t)) return null
  return Math.max(0, Date.now() - t)
}

// { allowed, reason, ageMs, hoursLeft } — the single source of truth for the
// button's enabled state, its tooltip and the confirmation copy. `row` is
// optional: pass the company row and a lead that arrived enriched from the
// import is recognised as researched instead of reading as a first run.
export function reenrichState(rowKey, dossier, row) {
  const ageMs = enrichedAgeMs(rowKey, dossier)
  if (ageMs == null) {
    // Enriched but we hold no dated dossier — a teammate ran it, the row came
    // researched in the import, or it predates the dossier store. Treat it as
    // enriched with an unknown age: still ALLOWED (the findings may well be
    // stale, and a rep must be able to re-run) but never silently, so the cost
    // of another research cycle is stated first.
    const imported = rowEnrichedFlag(row)
    if (imported || isEnriched(rowKey)) {
      return {
        allowed: true,
        first: false,
        ageMs: null,
        hoursLeft: 0,
        age: imported ? 'from the imported dataset' : 'already researched, date unknown',
      }
    }
    return { allowed: true, first: true, reason: '', ageMs: null, hoursLeft: 0 }
  }
  if (ageMs >= REENRICH_AFTER_MS) {
    const days = Math.floor(ageMs / 86400000)
    const hours = Math.floor(ageMs / 3600000)
    return {
      allowed: true,
      first: false,
      ageMs,
      hoursLeft: 0,
      age: days >= 1 ? `${days} day${days === 1 ? '' : 's'} old` : `${hours} hours old`,
    }
  }
  const hoursLeft = Math.max(1, Math.ceil((REENRICH_AFTER_MS - ageMs) / 3600000))
  return {
    allowed: false,
    first: false,
    ageMs,
    hoursLeft,
    reason: `Researched less than a day ago — re-enrich available in ${hoursLeft}h`,
  }
}
